import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, withClientTimeout } from "../lib/api";
import type {
  PublishingDelivery,
  PublishingDraft,
  PublishingMedia,
  PublishingPage,
  PublishingPrepareInput,
  PublishingTarget,
  PublishingVariant,
} from "../../../shared/publishing-studio";
import type {
  YouTubeRefreshInput,
  YouTubeRefreshReceipt,
  YouTubeReport,
  YouTubeReportKey,
  YouTubeVideo,
  YouTubeVideoPage,
} from "../../../shared/youtube-studio";

export const studioBase = (brandId: string) =>
  `/api/v1/brands/${encodeURIComponent(brandId)}`;
type Envelope<T> = { success: true; data: T };
export function useStudioRead<T>(
  brandId: string,
  path: string,
  enabled = true,
  poll = false,
) {
  return useQuery({
    queryKey: ["publishing-studio", brandId, path],
    enabled: !!brandId && enabled,
    queryFn: () =>
      withClientTimeout(
        async (signal) =>
          (await api.get<Envelope<T>>(`${studioBase(brandId)}${path}`, signal))
            .data,
      ),
    refetchInterval: poll ? 5_000 : false,
  });
}
export function useStudioMutation<T, I>(
  brandId: string,
  path: string | ((input: I) => string),
  method: "POST" | "PUT" = "POST",
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) =>
      withClientTimeout(async (signal) => {
        const url = `${studioBase(brandId)}${typeof path === "function" ? path(input) : path}`;
        return (
          method === "PUT"
            ? await api.put<Envelope<T>>(url, input, signal)
            : await api.post<Envelope<T>>(url, input, undefined, signal)
        ).data;
      }, 60_000),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["publishing-studio", brandId] }),
  });
}
export const usePublishingTargets = (brandId: string) =>
  useStudioRead<PublishingTarget[]>(brandId, "/publishing/targets");
function useStudioCollection<T>(brandId: string, kind: "drafts" | "media") {
  const result = useInfiniteQuery({
    queryKey: ["publishing-studio", brandId, `/publishing/${kind}`, "pages"],
    enabled: !!brandId,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      withClientTimeout(
        async (signal) =>
          (
            await api.get<Envelope<PublishingPage<T>>>(
              `${studioBase(brandId)}/publishing/${kind}?limit=20&offset=${pageParam}`,
              signal,
            )
          ).data,
      ),
    getNextPageParam: (page) =>
      page.has_more ? page.offset + page.limit : undefined,
  });
  return {
    ...result,
    data: result.data
      ? {
          ...result.data.pages[0]!,
          items: result.data.pages.flatMap((p) => p.items),
        }
      : undefined,
  };
}
export const usePublishingDrafts = (brandId: string) =>
  useStudioCollection<PublishingDraft>(brandId, "drafts");
export const usePublishingMedia = (brandId: string) =>
  useStudioCollection<PublishingMedia>(brandId, "media");
export const usePublishingDraft = (brandId: string, id: string) =>
  useStudioRead<PublishingDraft>(
    brandId,
    `/publishing/drafts/${encodeURIComponent(id)}`,
    !!id,
  );
export const usePublishingMediaItem = (brandId: string, id: string) =>
  useStudioRead<PublishingMedia>(
    brandId,
    `/publishing/media/${encodeURIComponent(id)}`,
    !!id,
  );
export const usePublishingVariants = (brandId: string, offset = 0) =>
  useStudioRead<PublishingPage<PublishingVariant>>(
    brandId,
    `/publishing/variants?limit=20&offset=${offset}`,
  );
export const usePublishingVariant = (brandId: string, id?: string) =>
  useStudioRead<PublishingVariant>(
    brandId,
    `/publishing/variants/${encodeURIComponent(id ?? "")}`,
    !!id,
  );
export const usePreparePublishing = (brandId: string) =>
  useStudioMutation<PublishingVariant, PublishingPrepareInput>(
    brandId,
    "/publishing/variants",
  );
export const usePublishingDelivery = (brandId: string, id?: string) =>
  useStudioRead<PublishingDelivery>(
    brandId,
    `/publishing/deliveries/${encodeURIComponent(id ?? "")}`,
    !!id,
    true,
  );
export const usePublishingDeliveries = (brandId: string) =>
  useStudioRead<PublishingPage<PublishingDelivery>>(
    brandId,
    "/publishing/deliveries?limit=20&offset=0",
    true,
    true,
  );
export function useYouTubeVideos(
  brandId: string,
  assignmentId: string,
  search: string,
  offset = 0,
) {
  const q = new URLSearchParams({
    assignment_id: assignmentId,
    search,
    limit: "20",
    offset: String(offset),
  });
  return useStudioRead<YouTubeVideoPage>(
    brandId,
    `/youtube/videos?${q}`,
    !!assignmentId,
  );
}
export const useYouTubeVideo = (
  brandId: string,
  assignmentId: string,
  videoId: string,
) =>
  useStudioRead<YouTubeVideo>(
    brandId,
    `/youtube/videos/${encodeURIComponent(videoId)}?assignment_id=${encodeURIComponent(assignmentId)}`,
    !!assignmentId && !!videoId,
  );
export function useYouTubeReport(
  brandId: string,
  assignmentId: string,
  key: YouTubeReportKey,
  start: string,
  end: string,
  videoId?: string,
  offset = 0,
) {
  const q = new URLSearchParams({
    assignment_id: assignmentId,
    report_key: key,
    start_date: start,
    end_date: end,
    limit: "20",
    offset: String(offset),
  });
  if (videoId) q.set("video_id", videoId);
  return useStudioRead<YouTubeReport>(
    brandId,
    `/youtube/reports?${q}`,
    !!assignmentId && (!key.startsWith("youtube.video_") || !!videoId),
  );
}
export function useRefreshYouTube(brandId: string) {
  const qc = useQueryClient();
  const [runs, setRuns] = useState<string[]>([]);
  const mutation = useStudioMutation<
    YouTubeRefreshReceipt,
    YouTubeRefreshInput
  >(brandId, "/youtube/refresh");
  const statuses = useQueries({
    queries: runs.map((runId) => ({
      queryKey: ["youtube-refresh", brandId, runId],
      queryFn: () =>
        withClientTimeout(
          async (signal) =>
            (
              await api.get<Envelope<YouTubeRefreshReceipt>>(
                `${studioBase(brandId)}/youtube/refresh/${encodeURIComponent(runId)}`,
                signal,
              )
            ).data,
        ),
      refetchInterval: (query: {
        state: { data?: YouTubeRefreshReceipt; error: unknown };
      }) =>
        query.state.error ||
        ["completed", "partial", "failed"].includes(
          query.state.data?.state ?? "",
        )
          ? false
          : 3_000,
    })),
  });
  const states = statuses.map((s) => s.data?.state).join(",");
  useEffect(() => {
    if (states.includes("completed") || states.includes("partial"))
      void qc.invalidateQueries({ queryKey: ["publishing-studio", brandId] });
  }, [states, brandId, qc]);
  async function mutateAsync(input: YouTubeRefreshInput) {
    const receipt = await mutation.mutateAsync(input);
    setRuns((ids) =>
      ids.includes(receipt.run_id) ? ids : [...ids, receipt.run_id],
    );
    return receipt;
  }
  const latest = statuses.at(-1);
  return {
    ...mutation,
    mutateAsync,
    data: latest?.data ?? mutation.data,
    error:
      mutation.error ??
      statuses.find((s) => s.error)?.error ??
      (statuses.some((s) => s.data?.state === "failed")
        ? new Error(
            "YouTube refresh failed. Check channel access and retry the affected report.",
          )
        : null),
    isPending:
      mutation.isPending ||
      statuses.some(
        (s) =>
          !s.error &&
          (!s.data || s.data.state === "queued" || s.data.state === "running"),
      ),
  };
}
